// @ts-nocheck
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { SplitOrderCartNotesModel, SplitOrderModel } from "@store/Framework/Types/ApiModels";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import updateSplitOrderCartNotes from "@store/Framework/Store/Greif/Pages/ReviewAndSubmit/SplitOrderCartLine/Handlers/UpdateSplitOrderCartNotes";
import useAccordionState from "@store/Framework/Common/Hooks/useAccordionState";
import { CartLinesDeliveryDate } from "@store/Library/Components/Cart/CartLinesDates";
import Plus from "@store/Library/Components/Toolkit/Icons/Plus";
import TrashFill from "@store/Library/Components/Toolkit/Icons/Trash/TrashFill";
import { reducedMotionStyles } from "@store/Themes/common/mixins/common";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles } from "@toolkit/Themes/common/mixins";
import React, { ChangeEvent, FC, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { css, keyframes } from "styled-components";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const fadeInCss = css`
    display: inline-flex;
    align-items: center;
    animation: ${fadeIn} 200ms ease-in-out;

    ${reducedMotionStyles("animation")}
`;
interface OwnProps {
    splitOrder: SplitOrderModel & Cart;
    maxLength?: number;
}

export interface CheckoutSplitOrderNotesPresentationProps extends ExpandStyles<CheckoutSplitOrderNotesStyles> {}

type Props = OwnProps & TextAreaProps & CheckoutSplitOrderNotesPresentationProps;

export interface CheckoutSplitOrderNotesStyles {
    header?: InjectableCss;
    trigger?: ClickablePresentationProps;
    triggerIcon?: IconPresentationProps;
    triggerLabel?: TypographyPresentationProps;
    removeButton?: ClickablePresentationProps;
    removeButtonIcon?: IconPresentationProps;
    removeButtonWrapper?: InjectableCss;
    panelTrack?: InjectableCss;
    panelTrackContent?: InjectableCss;
    textAreaWrapper?: InjectableCss;
    textArea?: TextAreaProps;
}

export const checkoutSplitOrderNotesStyles: CheckoutSplitOrderNotesStyles = {
    header: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: ${pxToRem(16)};
            padding: ${pxToRem(16)} ${pxToRem(24)};
        `,
    },
    trigger: {
        css: css`
            ${focusStyles()}
            display: flex;
            flex: 1;
            align-items: center;
            font-size: ${x => x.theme.design.typography.size.base};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            color: ${getColor("common.text")};
        `,
    },
    triggerIcon: {
        size: 24,
        color: "inherit",
        css: css`
            overflow: hidden;
            transition:
                width 200ms ease-in-out,
                margin-right 200ms ease-in-out,
                opacity 150ms ease-in-out;

            ${reducedMotionStyles()}
        `,
    },
    triggerLabel: {
        css: fadeInCss,
    },
    removeButton: {
        css: css`
            ${focusStyles()}
            display: inline-flex;
            align-items: center;
            gap: ${pxToRem(2)};
            color: ${getColor("text.link")};
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    removeButtonIcon: {
        src: TrashFill,
        size: 16,
        color: "inherit",
    },
    removeButtonWrapper: {
        css: fadeInCss,
    },
    panelTrack: {
        css: css`
            display: grid;
            transition: grid-template-rows 250ms ease-in-out;

            ${reducedMotionStyles()}
        `,
    },
    panelTrackContent: {
        css: css`
            overflow: hidden;
            min-height: 0;
        `,
    },
    textAreaWrapper: {
        css: css`
            padding: 0 ${pxToRem(24)} ${pxToRem(24)};
        `,
    },
    textArea: {
        border: "rectangle",
        rows: 2,
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: ${pxToRem(80)};
            height: ${pxToRem(80)};
            padding: ${pxToRem(11)};
            background-color: ${getColor("common.background")};
            border-color: ${getColor("common.border")};
            resize: none;

            &::placeholder {
                color: ${getColor("common.border")};
            }

            && {
                border-radius: ${pxToRem(6)};
            }
        `,
    },
};

const CheckoutSplitOrderNotes: FC<Props> = ({
    splitOrder,
    maxLength: orderLineMaxLength,
    extendedStyles,
    overridedStyles,
}) => {
    const expandedStyles = useExpandProps(checkoutSplitOrderNotesStyles, extendedStyles, overridedStyles);
    const [splitOrderNotes, setSplitOrderNotes] = useState<string>(splitOrder.notes);
    const { isUpdatingNotes } = useSelector(
        (state: ApplicationState) => state.greif.pages.reviewAndSubmit.splitOrderCartLine,
    );
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);
    const {
        isExpanded: isAccordionExpanded,
        triggerId,
        panelId,
        toggle,
    } = useAccordionState(Boolean(splitOrder.notes));

    const styles = useMemo(
        () =>
            mergeProps<CheckoutSplitOrderNotesStyles>(expandedStyles, {
                triggerIcon: {
                    css: css`
                        width: ${isAccordionExpanded ? 0 : pxToRem(24)};
                        margin-right: ${isAccordionExpanded ? 0 : pxToRem(8)};
                        opacity: ${isAccordionExpanded ? 0 : 1};
                    `,
                },
                panelTrack: {
                    css: css`
                        grid-template-rows: ${isAccordionExpanded ? "1fr" : "0fr"};
                    `,
                },
            }),
        [expandedStyles, isAccordionExpanded],
    );

    const deliveryDateIds = splitOrder.lines.map(line => {
        const { deliveryDate } = line.properties;
        const deliveryDateObj: CartLinesDeliveryDate = deliveryDate ? JSON.parse(deliveryDate) : {};

        return deliveryDateObj?.id;
    });

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setSplitOrderNotes(event.currentTarget.value);
    };

    const persistNotes = (notes: string, onError?: () => void) => {
        dispatch(
            updateSplitOrderCartNotes({
                splitOrderCartNotes: {
                    cartId: splitOrder.id,
                    deliveryDateIds,
                    notes,
                } as SplitOrderCartNotesModel,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete() {
                    toasterContext.addToast({
                        body: translate("Notes failed to be updated"),
                        messageType: "danger",
                    });
                    onError?.();
                },
            }),
        );
    };

    const notesBlurHandler = () => {
        if (splitOrderNotes !== splitOrder.notes) {
            persistNotes(splitOrderNotes);
        }
    };

    const triggerClickHandler = () => {
        if (!isAccordionExpanded) {
            toggle();
        }
    };

    const removeNotesHandler = (event: React.MouseEvent) => {
        event.preventDefault();

        const previousNotes = splitOrderNotes;
        const shouldPersistRemoval = Boolean(splitOrderNotes || splitOrder.notes);

        setSplitOrderNotes("");
        toggle();

        if (shouldPersistRemoval) {
            persistNotes("", () => {
                setSplitOrderNotes(previousNotes);
                toggle();
            });
        }
    };

    const notesLength = splitOrderNotes.length;

    const hintText =
        // When 80% of the defined value is reached, show the textarea hint with the caracter counter.
        orderLineMaxLength && notesLength > orderLineMaxLength * 0.8
            ? translate(`{0} of {1} characters`, String(notesLength), String(orderLineMaxLength))
            : null;

    return (
        <LoadingOverlay loading={isUpdatingNotes}>
            <StyledWrapper {...styles.header}>
                <Clickable
                    {...styles.trigger}
                    id={triggerId}
                    aria-controls={panelId}
                    aria-expanded={isAccordionExpanded}
                    onClick={triggerClickHandler}
                >
                    <IconMemo {...styles.triggerIcon} src={Plus} />
                    <Typography key={isAccordionExpanded ? "expanded" : "collapsed"} {...styles.triggerLabel}>
                        {translate(isAccordionExpanded ? "Order Notes" : "Add Order Notes")}
                    </Typography>
                </Clickable>
                {isAccordionExpanded && (
                    <StyledWrapper key="removeButton" {...styles.removeButtonWrapper}>
                        <Clickable
                            {...styles.removeButton}
                            aria-label={translate("Remove")}
                            onMouseDown={(event: React.MouseEvent<HTMLElement>) => event.preventDefault()}
                            onClick={removeNotesHandler}
                        >
                            <IconMemo {...styles.removeButtonIcon} />
                            {translate("Remove")}
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.panelTrack}>
                <StyledWrapper {...styles.panelTrackContent}>
                    <StyledWrapper
                        {...styles.textAreaWrapper}
                        id={panelId}
                        aria-labelledby={triggerId}
                        aria-hidden={!isAccordionExpanded}
                    >
                        <TextArea
                            {...styles.textArea}
                            aria-label={translate("Order Notes")}
                            placeholder={translate("Start typing here...")}
                            value={splitOrderNotes}
                            onChange={notesChangeHandler}
                            onBlur={notesBlurHandler}
                            maxLength={orderLineMaxLength}
                            hint={hintText}
                            tabIndex={isAccordionExpanded ? undefined : -1}
                            disabled={!isAccordionExpanded}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default CheckoutSplitOrderNotes;
